"use client";

import { useEffect, useId, type ReactNode } from "react";
import { createPortal } from "react-dom";

type MobileBottomSheetProps = {
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
  /**
   * Set false when the parent already locks `document.body` (e.g. LoginDialog mounts
   * mobile + desktop layers at once — double lock breaks cleanup and leaves scroll disabled).
   * @default true
   */
  lockBodyScroll?: boolean;
  /**
   * Visual classes (background tint + blur) for the dimmed tap layer.
   * Positioning (`absolute inset-0`) is always applied by the sheet itself,
   * so callers only need to pass color/blur (e.g. `"bg-black/55 backdrop-blur-md"`).
   */
  backdropClassName?: string;
};

export function MobileBottomSheet({
  open,
  title,
  closeLabel,
  onClose,
  children,
  lockBodyScroll = true,
  backdropClassName = "bg-black/45 backdrop-blur-[1px]",
}: MobileBottomSheetProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const prevOverflow = lockBodyScroll ? document.body.style.overflow : "";
    if (lockBodyScroll) {
      document.body.style.overflow = "hidden";
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      if (lockBodyScroll) {
        document.body.style.overflow = prevOverflow;
      }
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, lockBodyScroll]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col justify-end md:hidden">
      <button
        type="button"
        className={`absolute inset-0 ${backdropClassName}`}
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div
        className="animate-sheet-enter relative mt-auto flex max-h-[min(85vh,32rem)] flex-col rounded-t-2xl bg-white shadow-[0_-8px_30px_rgba(0,0,0,.12)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div
          className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-[#E5E5E5]"
          aria-hidden
        />
        <div className="flex items-center justify-between gap-3 border-b border-[#E5E5E5] px-4 py-3">
          <h2
            id={titleId}
            className="text-base font-semibold text-[#130F0E]"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F4F4F4]"
            aria-label={closeLabel}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto overscroll-contain md:p-2 p-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
